import { CommonModule } from '@angular/common';
import { Component, ViewChild } from '@angular/core';
import { PoTabsModule, PoPageModule } from '@po-ui/ng-components';

import { RegrasCteComponent } from './cte/cte';
import { RegrasNfseComponent } from './nfse/nfse';
import { RegrasNaturezaComponent } from './natureza/natureza';
import { RegrasProdutoComponent } from './produto/produto';
import { RegrasTomadorComponent } from './tomador/tomador';
import { AtalhoRegra, RegrasSimuladorComponent } from './simulador/simulador';

/** Identificador de cada aba, usado para a carga sob demanda. */
type Aba = 'cte' | 'nfse' | 'natureza' | 'produto' | 'tomador' | 'simulador';

/**
 * Cadastro de Regras de Classificação Automática — menu único.
 *
 * Os cinco cadastros (CT-e, NFS-e, natureza financeira, de-para de serviço e
 * tomadores) mais o simulador são abas de uma mesma tela, como os protótipos da
 * MIT044 apresentam, e não seis itens de menu.
 *
 * As abas carregam sob demanda: o conteúdo só é instanciado quando a aba vira
 * ativa. Sem isso, abrir a tela dispararia de uma vez as cinco listagens mais
 * os lookups de cada formulário — e quatro dessas chamadas seriam jogadas
 * fora, porque o usuário só olha uma aba por vez.
 *
 * O controle de acesso é do menu, não da aba: um único privilégio (rota
 * `/regras`) libera o conjunto.
 */
@Component({
  selector: 'app-regras',
  imports: [
    CommonModule,
    PoPageModule,
    PoTabsModule,
    RegrasCteComponent,
    RegrasNfseComponent,
    RegrasNaturezaComponent,
    RegrasProdutoComponent,
    RegrasTomadorComponent,
    RegrasSimuladorComponent
  ],
  templateUrl: './regras.html',
  styleUrls: ['./regras.css']
})
export class RegrasComponent {
  @ViewChild(RegrasCteComponent) private cte?: RegrasCteComponent;
  @ViewChild(RegrasNfseComponent) private nfse?: RegrasNfseComponent;
  @ViewChild(RegrasNaturezaComponent) private natureza?: RegrasNaturezaComponent;
  @ViewChild(RegrasProdutoComponent) private produto?: RegrasProdutoComponent;

  /** Abas já visitadas. Uma vez montada, a aba não é destruída ao trocar. */
  private visitadas = new Set<Aba>(['cte']);

  abaAtiva: Aba = 'cte';

  ativar(aba: Aba): void {
    this.abaAtiva = aba;
    this.visitadas.add(aba);
  }

  /**
   * Mantém no DOM as abas já abertas, para que voltar a uma delas não recarregue
   * a listagem nem perca o filtro que o usuário tinha aplicado.
   */
  montada(aba: Aba): boolean {
    return this.visitadas.has(aba);
  }

  /**
   * RF12 — abre no cadastro a regra que o simulador apontou, já filtrada pelo
   * código, para o usuário conferir ou ajustar sem procurá-la na listagem.
   *
   * O setTimeout não é enfeite: quando a aba de destino ainda não foi visitada,
   * o componente dela só existe depois do ciclo de detecção que a monta.
   */
  irParaRegra(atalho: AtalhoRegra): void {
    this.ativar(atalho.aba);
    setTimeout(() => this.filtrarNaAba(atalho));
  }

  private filtrarNaAba(atalho: AtalhoRegra): void {
    const alvo = {
      cte: this.cte,
      nfse: this.nfse,
      natureza: this.natureza,
      produto: this.produto
    }[atalho.aba];

    alvo?.filtrar(atalho.codigo);
  }
}
